import React, { useEffect, useState } from "react";
import {
  listLeagueDisputes,
  dismissLeagueDispute,
  voidLeagueDispute,
  flipLeagueDispute,
} from "../lib/api";

/**
 * LeagueModerationPanel — league-scoped dispute moderation surface.
 *
 * Renders inside a modal opened from the League page when the viewer
 * has owner / co_admin / moderator role on the league. Mirrors the
 * admin DisputesSection UX but only for THIS league and via the
 * non-admin /api/leagues/{id}/disputes endpoints.
 *
 * Actions per dispute:
 *   • DISMISS  → the recorded score stands.
 *   • VOID     → match is voided, MMR + score events unwound.
 *   • FLIP     → pick the OTHER team as winner; MMR re-applied.
 */
export function LeagueModerationPanel({ league, onClose, onToast }) {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [actionFor, setActionFor] = useState(null); // { challenge, action }
  const [note, setNote] = useState("");

  const load = async (s = status) => {
    if (!league?.league_id) return;
    setBusy(true);
    try {
      const res = await listLeagueDisputes(league.league_id, s);
      setRows(res.rows || []);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load disputes.");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [league?.league_id, status]);

  const runAction = async () => {
    if (!actionFor) return;
    setActiveId(actionFor.challenge.challenge_id);
    try {
      const c = actionFor.challenge;
      if (actionFor.action === "dismiss") {
        await dismissLeagueDispute(league.league_id, c.challenge_id, note);
        onToast?.("✅ Dispute dismissed.");
      } else if (actionFor.action === "void") {
        await voidLeagueDispute(league.league_id, c.challenge_id, note);
        onToast?.("🚫 Match voided.");
      } else if (actionFor.action === "flip") {
        // Flip → the OTHER team becomes winner.
        const new_winner =
          c.winner_team_id === c.from_team_id ? c.to_team_id : c.from_team_id;
        await flipLeagueDispute(league.league_id, c.challenge_id, {
          new_winner_team_id: new_winner,
          note,
        });
        onToast?.("🔁 Winner flipped, MMR adjusted.");
      }
      setActionFor(null);
      setNote("");
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Action failed.");
    } finally { setActiveId(""); }
  };

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/85 backdrop-blur-md p-2 sm:p-4 flex items-center justify-center"
      onClick={onClose}
      data-testid="league-moderation-backdrop"
    >
      <div
        className="glass neon-glow rounded-3xl p-4 sm:p-6 w-full max-w-3xl max-h-[94vh] max-h-[94dvh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="league-moderation-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
              <i className="fas fa-gavel mr-1.5" />MODERATION
            </p>
            <h2 className="font-display text-2xl grad-text">
              {league?.name} · DISPUTES
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="league-moderation-close"
            className="w-9 h-9 rounded-xl bg-black/40 border border-white/15 text-white/70 hover:text-white"
            aria-label="Close moderation panel"
          >
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="flex gap-1.5 mb-3" data-testid="moderation-status-filter">
          {[
            { id: "open",      label: "OPEN"     },
            { id: "resolved",  label: "RESOLVED" },
            { id: "voided",    label: "VOIDED"   },
            { id: "all",       label: "ALL"      },
          ].map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStatus(s.id)}
              data-testid={`moderation-status-${s.id}`}
              className={`text-[10px] tracking-widest px-3 py-1.5 rounded-lg border ${
                status === s.id
                  ? "bg-[#14b8a6]/20 text-[#14b8a6] border-[#14b8a6]/55"
                  : "bg-black/40 text-white/65 border-white/15 hover:border-[#14b8a6]/40"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <div className="glass rounded-2xl p-6 text-center" data-testid="moderation-empty">
            <i className="fas fa-circle-check text-3xl text-[#14b8a6] mb-2" />
            <p className="text-sm text-white/80">
              {status === "open"
                ? "No open disputes — quiet on the moderation front."
                : "Nothing in this bucket yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="moderation-rows">
            {rows.map((r) => (
              <DisputeRow
                key={r.challenge_id}
                row={r}
                busy={activeId === r.challenge_id}
                onAct={(action) => { setActionFor({ challenge: r, action }); setNote(""); }}
              />
            ))}
          </div>
        )}

        {actionFor && (
          <div className="fixed inset-0 z-[100] bg-black/80 p-3 flex items-center justify-center"
               onClick={() => setActionFor(null)}>
            <div
              className="glass rounded-2xl p-5 w-full max-w-md neon-glow"
              onClick={(e) => e.stopPropagation()}
              data-testid="moderation-confirm-modal"
            >
              <p className="text-[10px] tracking-[3px] text-[#14b8a6] font-bold">
                <i className={`fas ${
                  actionFor.action === "dismiss" ? "fa-check-circle" :
                  actionFor.action === "void" ? "fa-ban" : "fa-rotate"
                } mr-1.5`} />
                {actionFor.action.toUpperCase()} DISPUTE
              </p>
              <p className="text-sm text-white mt-2 mb-3">
                {actionFor.action === "dismiss" && "Recorded score will stand. Reporter is notified."}
                {actionFor.action === "void"    && "Match is voided. MMR + score events are unwound. Cannot be undone."}
                {actionFor.action === "flip"    && "Winner is flipped to the OTHER team. MMR is re-applied."}
              </p>
              <label className="block text-[10px] tracking-widest text-white/55 mb-1">NOTE (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                maxLength={600}
                placeholder="Why are you taking this action? (visible in audit log)"
                data-testid="moderation-confirm-note"
                className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm mb-3"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setActionFor(null)}
                  data-testid="moderation-confirm-cancel"
                  className="btn-ghost rounded-xl px-4 py-2 text-[11px] tracking-widest"
                >
                  CANCEL
                </button>
                <button
                  type="button"
                  onClick={runAction}
                  disabled={busy}
                  data-testid="moderation-confirm-submit"
                  className={`rounded-xl px-4 py-2 text-[11px] tracking-widest disabled:opacity-40 ${
                    actionFor.action === "void"
                      ? "bg-red-500/20 text-red-300 border border-red-500/40"
                      : "btn-neon"
                  }`}
                >
                  CONFIRM {actionFor.action.toUpperCase()}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DisputeRow({ row, busy, onAct }) {
  const a = row.team_a || { name: row.from_team_id, team_id: row.from_team_id };
  const b = row.team_b || { name: row.to_team_id,   team_id: row.to_team_id };
  const winner = row.winner_team_id === a.team_id ? a : b;
  const isResolved = !row.disputed;

  return (
    <div
      data-testid={`moderation-row-${row.challenge_id}`}
      className={`glass rounded-2xl p-3 border ${
        row.disputed ? "border-yellow-400/45" : "border-white/8"
      }`}
    >
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <p className="text-[10px] tracking-[3px] text-white/45 mb-1">
            CHALLENGE · <span className="font-mono">{row.challenge_id}</span>
          </p>
          <p className="text-sm font-bold text-white">
            {a.name} <span className="text-white/40 mx-2">vs</span> {b.name}
          </p>
          <p className="text-[11px] text-white/70 mt-1">
            Score: <span className="font-mono">{row.score_a ?? "—"}-{row.score_b ?? "—"}</span>
            <span className="mx-2">·</span>
            Winner: <span className="text-[#14b8a6] font-bold">{winner?.name || "?"}</span>
          </p>
          {row.dispute_opener && (
            <p className="text-[10px] text-white/40 mt-1">
              <i className="fas fa-flag mr-1" />Opened by {row.dispute_opener.name}
            </p>
          )}
          {row.dispute_resolution && (
            <p className="text-[10px] text-[#14b8a6] mt-1">
              <i className="fas fa-circle-check mr-1" />
              Resolved: {row.dispute_resolution.toUpperCase()}
              {row.dispute_admin_note ? ` — "${row.dispute_admin_note}"` : ""}
            </p>
          )}
        </div>
        {!isResolved && (
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={() => onAct("dismiss")}
              disabled={busy}
              data-testid={`moderation-dismiss-${row.challenge_id}`}
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg btn-ghost"
            >
              <i className="fas fa-check mr-1" />DISMISS
            </button>
            <button
              type="button"
              onClick={() => onAct("flip")}
              disabled={busy}
              data-testid={`moderation-flip-${row.challenge_id}`}
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/45"
            >
              <i className="fas fa-rotate mr-1" />FLIP
            </button>
            <button
              type="button"
              onClick={() => onAct("void")}
              disabled={busy}
              data-testid={`moderation-void-${row.challenge_id}`}
              className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-red-500/15 text-red-300 border border-red-500/45"
            >
              <i className="fas fa-ban mr-1" />VOID
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default LeagueModerationPanel;
